import React, { useEffect, useRef, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Trash2, Copy, ImagePlus, Check } from "lucide-react";

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export function MediaGrid({ onPick }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get("/admin/media").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      toast.error("Only JPEG, PNG, WebP or GIF images are allowed"); return;
    }
    if (file.size > 5_000_000) { toast.error("Image too large (max 5MB)"); return; }
    setUploading(true);
    try {
      const data = await fileToDataUrl(file);
      const r = await api.post("/admin/media", { data, filename: file.name, alt: "" });
      setItems((s) => [r.data, ...s]);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/media/${id}`);
    setItems((s) => s.filter((x) => x.id !== id));
    toast.success("Image removed");
  };

  const copy = (u) => { navigator.clipboard?.writeText(mediaUrl(u)); toast.success("URL copied"); };

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={onFile} data-testid="media-file-input" />
      <Button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="media-upload-btn" className="rounded-full bg-primary text-primary-foreground mb-5">
        {uploading ? <>Uploading…</> : <><Upload className="h-4 w-4 mr-1.5" /> Upload image</>}
      </Button>
      {items.length === 0 && <p className="text-sm text-muted-foreground">No images yet. Upload your first one.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((m) => (
          <div key={m.id} data-testid={`media-item-${m.id}`} className="rounded-xl border border-border overflow-hidden bg-card group">
            <button type="button" onClick={() => onPick && onPick(m)} className={`block w-full ${onPick ? "cursor-pointer" : "cursor-default"}`}>
              <img src={mediaUrl(m.url)} alt={m.alt || ""} className="h-32 w-full object-cover" />
            </button>
            <div className="flex items-center justify-between px-2 py-1.5 gap-1">
              {onPick ? (
                <Button size="sm" variant="ghost" onClick={() => onPick(m)} data-testid={`media-pick-${m.id}`} className="h-8 px-2 text-primary"><Check className="h-3.5 w-3.5 mr-1" /> Use</Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => copy(m.url)} data-testid={`media-copy-${m.id}`} className="h-8 px-2"><Copy className="h-3.5 w-3.5" /></Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => remove(m.id)} data-testid={`media-delete-${m.id}`} className="h-8 px-2 text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MediaPickerDialog({ open, onOpenChange, onSelect }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="media-picker-dialog">
        <DialogHeader><DialogTitle className="font-serif flex items-center gap-2"><ImagePlus className="h-5 w-5 text-accent" /> Choose an image</DialogTitle></DialogHeader>
        <MediaGrid onPick={(m) => { onSelect(mediaUrl(m.url)); onOpenChange(false); }} />
      </DialogContent>
    </Dialog>
  );
}

export default function AdminMediaLibrary() {
  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="admin-media-panel">
      <h3 className="font-serif text-lg font-semibold mb-1">Media Library</h3>
      <p className="text-xs text-muted-foreground mb-4">Upload images to reuse across pages and content. JPEG / PNG / WebP / GIF, up to 5MB.</p>
      <MediaGrid />
    </div>
  );
}
